import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';
import { fetchTeachersInClassroom, TeacherInClassroom } from '../../../lib/classroomsApi';
import { parseApiError } from '../utils';

interface Props {
  classroomId: number;
}

const ProfesoriPanel: React.FC<Props> = ({ classroomId }) => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<TeacherInClassroom[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [openTeacherId, setOpenTeacherId] = useState<number | null>(null);

  const loadTeachers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTeachersInClassroom(classroomId);

      // Fallback: daca backend-ul nu returneaza materiile pe profesori,
      // le construim din lista de materii a clasei (are professor_id).
      let next = data;
      const missingSubjects = next.some((t) => !Array.isArray(t.subject_names) && !Array.isArray(t.subjects));
      if (missingSubjects) {
        try {
          const resp = await api.get(`/api/materii/subjects/by-class/${classroomId}`);
          const subjects = Array.isArray(resp.data) ? resp.data : [];
          const namesByTeacher = new Map<number, string[]>();
          for (const s of subjects) {
            const pid = Number(s?.professor_id);
            const name = (s?.name || '').toString().trim();
            if (!pid || !name) continue;
            const list = namesByTeacher.get(pid) || [];
            list.push(name);
            namesByTeacher.set(pid, list);
          }
          next = next.map((t) => {
            const names = Array.from(new Set(namesByTeacher.get(t.id) || [])).sort((a, b) => a.localeCompare(b));
            if (names.length === 0) return t;
            return { ...t, subject_names: names };
          });
        } catch {
          // ignore fallback errors
        }
      }

      setTeachers(next);
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId]);

  const sortedTeachers = useMemo(() => {
    return [...teachers].sort((a, b) => Number(b.is_admin) - Number(a.is_admin) || a.full_name.localeCompare(b.full_name));
  }, [teachers]);

  const openMessage = (t: TeacherInClassroom) => {
    const params = new URLSearchParams({
      dm: String(t.id),
      name: t.full_name,
      role: 'teacher',
    });
    navigate(`/communication-center?${params.toString()}`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-foreground">Profesori</h2>
        <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={loadTeachers} disabled={loading}>
          Reîncarcă
        </Button>
      </div>

      {info && <div className="mb-4 bg-success/10 border border-success/20 text-success rounded-md p-3 text-sm">{info}</div>}
      {error && <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}

      {loading ? (
        <div className="text-sm text-muted-foreground">Se încarcă profesorii…</div>
      ) : sortedTeachers.length === 0 ? (
        <div className="text-sm text-muted-foreground">Nu există profesori în această clasă.</div>
      ) : (
        <div className="divide-y divide-border">
          {sortedTeachers.map((t) => {
            const isOpen = openTeacherId === t.id;
            return (
              <div key={t.id} className="py-2">
                <div className="flex items-start justify-between gap-3">
                  <button
                    className="flex-1 min-w-0 text-left rounded-md hover:bg-muted/40 transition-colors duration-150 px-2 py-2"
                    onClick={() => setOpenTeacherId((prev) => (prev === t.id ? null : t.id))}
                    type="button"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-foreground truncate">{t.full_name}</p>
                          {t.is_admin && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                              Admin
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{t.email || '—'}</div>
                      </div>

                      <Icon
                        name="ChevronDown"
                        size={18}
                        className={`text-muted-foreground transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </button>

                  <Button variant="outline" iconName="MessageSquare" iconPosition="left" onClick={() => openMessage(t)}>
                    Mesaj
                  </Button>
                </div>

                {isOpen && (
                  <div className="mt-2 px-2 pb-2">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-muted/20 border border-border rounded-lg p-4">
                      <div>
                        <div className="text-xs text-muted-foreground">Email</div>
                        <div className="text-sm text-foreground break-words">{t.email || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Telefon</div>
                        <div className="text-sm text-foreground">{t.phone || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Titluri</div>
                        <div className="text-sm text-foreground">
                          {Array.isArray(t.titles) && t.titles.length > 0 ? t.titles.join(', ') : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Materii</div>
                        <div className="text-sm text-foreground">
                          {Array.isArray(t.subject_names) && t.subject_names.length > 0
                            ? t.subject_names.join(', ')
                            : Array.isArray(t.subjects) && t.subjects.length > 0
                              ? Array.from(
                                  new Set(t.subjects.map((s) => (s?.name || '').toString().trim()).filter((n) => !!n))
                                ).join(', ')
                              : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default ProfesoriPanel;
